import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { pageTitle } from "@assets/constants";
import {
  LoginState,
  UserAccess,
  UserProfileViewModel,
  Company,
} from "@assets/types";
import AuthService from "@services/AuthService";
import UserService from "@services/UserService";
import type { RootState } from "@state/store";
import i18next from "i18next";

export const login = createAsyncThunk("auth/login", (loginState: LoginState) =>
  AuthService.login(loginState)
);

export const logout = createAsyncThunk("auth/logout", (userAuth: UserAccess) =>
  AuthService.logout(userAuth)
);

export const getProfile = createAsyncThunk("auth/user_me", () =>
  UserService.getUserProfileFromAPI()
);

export const switchCompany = createAsyncThunk(
  "user/selectCompany",
  (partnerCode: string) => UserService.changeUserCompany(partnerCode)
);

export const selectLanguage = createAsyncThunk(
  "user/selectLanguage",
  async (language: string) => {
    window.localStorage.setItem("selectedLanguage", language);
    document.title = `Samskip - ${pageTitle[language]}`;
    await i18next.changeLanguage(language);
    if (UserService.getUserId()) {
      await UserService.updateSelectedLanguageWebSetting(language);
    }
  }
);

export interface UserProfileState {
  loginInProgress: boolean;
  logoutInProgress: boolean;
  profileLoaded: boolean;
  userProfile?: UserProfileViewModel;
  isPasswordModalOpen: boolean;
  isSwitchingCompanies: boolean;
}

export const initialUserProfileState: UserProfileState = {
  loginInProgress: false,
  logoutInProgress: false,
  profileLoaded: false,
  userProfile: undefined,
  isPasswordModalOpen: false,
  isSwitchingCompanies: false,
};

export const userProfileSlice = createSlice({
  name: "userProfile",
  initialState: initialUserProfileState,
  reducers: {
    openPasswordModal: (state) => {
      state.isPasswordModalOpen = true;
    },
    closePasswordModal: (state) => {
      state.isPasswordModalOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout
      .addCase(logout.pending, (state) => {
        state.logoutInProgress = true;
      })
      .addCase(logout.rejected, (state) => {
        state.logoutInProgress = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.userProfile = undefined;
        state.profileLoaded = false;
        state.logoutInProgress = false;
      })
      // Get profile
      .addCase(getProfile.rejected, (state) => {
        state.userProfile = undefined;
        state.profileLoaded = true;
      })
      .addCase(
        getProfile.fulfilled,
        (state, action: PayloadAction<UserProfileViewModel>) => {
          state.userProfile = action.payload;
          state.profileLoaded = true;
        }
      )
      // Login
      .addCase(login.pending, (state) => {
        state.loginInProgress = true;
      })
      .addCase(login.rejected, (state) => {
        state.profileLoaded = false;
        state.loginInProgress = false;
      })
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<UserProfileViewModel>) => {
          state.userProfile = action.payload;
          state.profileLoaded = true;
          state.loginInProgress = false;
        }
      )
      // Company switch
      .addCase(switchCompany.pending, (state) => {
        state.isSwitchingCompanies = true;
      })
      .addCase(
        switchCompany.fulfilled,
        (state, action: PayloadAction<Company>) => {
          state.isSwitchingCompanies = false;
          if (state.userProfile) {
            state.userProfile.SelectedCompany = action.payload;
          }
        }
      )
      .addCase(switchCompany.rejected, (state) => {
        state.isSwitchingCompanies = false;
      });
  },
});

export const { openPasswordModal, closePasswordModal } =
  userProfileSlice.actions;

export const userProfileState = (state: RootState) => state.userProfileSlice;

export default userProfileSlice.reducer;
